/**
 * OrderView Component
 * 
 * Modal displaying order details in invoice format.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 */

import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { X, Home, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { getImageUrl, formatMonDD } from '../../utils/formatters';

function OrderView({ order, onClose, currentStore, onNavigatePrev, onNavigateNext, currentIndex, totalOrders }) {
  const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;
  
  // Check if navigation is available
  const hasNavigation = typeof currentIndex === 'number' && typeof totalOrders === 'number';
  const hasPrev = hasNavigation && currentIndex > 0;
  const hasNext = hasNavigation && currentIndex < totalOrders - 1;
  
  const downloadInvoice = async (invoiceType) => {
    console.log('Download invoice clicked:', invoiceType);
    console.log('Order ID:', order.id);
    console.log('API URL:', API_URL);
    
    try {
      const url = `${API_URL}/orders/${order.id}/generate-invoice?invoice_type=${invoiceType}`;
      console.log('Requesting:', url);
      
      toast.info(`Generating ${invoiceType} invoice...`);
      
      const response = await axios.post(
        url,
        {},
        { responseType: 'blob' }
      );
      
      console.log('Response received:', response);
      console.log('Response headers:', response.headers);
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `bawarchi_${invoiceType}_invoice_${order.id.substring(0, 8)}.pdf`; // fallback
      
      console.log('Content-Disposition header:', contentDisposition);
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          console.log('Extracted filename from header:', filename);
        }
      } else {
        console.log('No Content-Disposition header found, using fallback filename');
      }
      
      console.log('Final filename for download:', filename);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${invoiceType === 'customer' ? 'Customer' : 'Kitchen'} invoice downloaded`);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to download invoice: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bawarchi-background-muted)' }}>
      {/* Navigation */}
      <div className="nav-bawarchi relative">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        
        {/* Order Navigation - Fixed center position */}
        {hasNavigation && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 absolute left-1/2 transform -translate-x-1/2">
            <button 
              onClick={onNavigatePrev}
              disabled={!hasPrev}
              className={`p-1 h-8 w-8 rounded flex items-center justify-center ${hasPrev ? 'hover:bg-gray-200' : 'opacity-40 cursor-not-allowed'}`}
              title="Previous order"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-2 min-w-[70px] text-center text-sm font-medium">
              {currentIndex + 1} / {totalOrders}
            </span>
            <button 
              onClick={onNavigateNext}
              disabled={!hasNext}
              className={`p-1 h-8 w-8 rounded flex items-center justify-center ${hasNext ? 'hover:bg-gray-200' : 'opacity-40 cursor-not-allowed'}`}
              title="Next order"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
        
        <button onClick={onClose} className="btn-bawarchi-secondary" style={{ padding: '8px 20px', minHeight: '40px' }}>
          <Home className="w-4 h-4 mr-2 inline" />
          Back
        </button>
      </div>

      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Order Details</h1>
          <p className="page-subtitle">Order ID: {order.id.substring(0, 12)}...</p>
        </div>

        <div className="flex gap-3 mb-6">
          <button onClick={() => downloadInvoice('customer')} className="btn-bawarchi-secondary" style={{ borderColor: 'var(--bawarchi-success)', color: 'var(--bawarchi-success)' }}>
            <Download className="w-4 h-4 mr-2 inline" />
            Customer Invoice
          </button>
          <button onClick={() => downloadInvoice('kitchen')} className="btn-bawarchi-secondary" style={{ borderColor: 'var(--bawarchi-info)', color: 'var(--bawarchi-info)' }}>
            <Download className="w-4 h-4 mr-2 inline" />
            Kitchen Invoice
          </button>
        </div>

        <div className="card-bawarchi">
          <div className="flex justify-between items-center" style={{ marginBottom: 'var(--spacing-6)' }}>
            <div>
              <h2 className="card-bawarchi-title" style={{ marginBottom: 'var(--spacing-2)' }}>Order Summary</h2>
              <p className="text-small text-muted">Complete order information</p>
            </div>
            <div className="flex gap-2" style={{ display: 'none' }}>
              <Button onClick={() => downloadInvoice('customer')} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                <Download className="w-4 h-4 mr-2" />
                Customer Invoice
              </Button>
              <Button onClick={() => downloadInvoice('kitchen')} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                <Download className="w-4 h-4 mr-2" />
                Kitchen Invoice
              </Button>
              <Button onClick={onClose} variant="outline" className="border-orange-300">
                <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </div>
          
          <div className="p-6 space-y-6">
            {/* Customer Information */}
            <div className="grid grid-cols-2 gap-6">
              <div className="card-bawarchi">
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: 'var(--spacing-4)', color: 'var(--bawarchi-primary)' }}>Customer Information</h3>
                <div className="space-y-2">
                  <div><strong>Name:</strong> {order.customer_info?.name || 'N/A'}</div>
                  <div><strong>Phone:</strong> {order.customer_info?.phone || 'N/A'}</div>
                  {order.customer_info?.email && <div><strong>Email:</strong> {order.customer_info.email}</div>}
                  <div><strong>Address:</strong> {order.customer_info?.address || 'N/A'}</div>
                  <div><strong>Occasion:</strong> {order.customer_info?.occasion || 'N/A'}</div>
                  <div><strong>Guests:</strong> {order.customer_info?.adults || 0} Adults, {order.customer_info?.kids || 0} Kids</div>
                  {order.customer_info?.prepared_by && <div><strong>Prepared By:</strong> {order.customer_info.prepared_by}</div>}
                  {order.customer_info?.lead_employee && <div><strong>Lead By:</strong> {order.customer_info.lead_employee}</div>}
                </div>
              </div>
              
              <div className="card-bawarchi">
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: 'var(--spacing-4)', color: 'var(--bawarchi-info)' }}>Event Details</h3>
                <div className="space-y-2">
                  <div><strong>Start Date:</strong> {order.customer_info?.start_date || 'N/A'}</div>
                  <div><strong>End Date:</strong> {order.customer_info?.end_date || 'N/A'}</div>
                  {order.customer_info?.day_meal_selections && order.customer_info.day_meal_selections.length > 0 && (
                    <div>
                      <strong>Time:</strong> {
                        order.customer_info.day_meal_selections.map((day, idx) => 
                          day.meal_times ? day.meal_times.map((mealTime, mIdx) => {
                            const time24 = mealTime.delivery_time || '';
                            let timeFormatted = 'N/A';
                            if (time24 && time24 !== 'N/A') {
                              try {
                                const parts = time24.split(':');
                                if (parts.length >= 2) {
                                  let hour = parseInt(parts[0]);
                                  const minute = parseInt(parts[1]);
                                  let period = 'AM';
                                  if (hour === 0) {
                                    hour = 12;
                                  } else if (hour >= 12) {
                                    period = 'PM';
                                    if (hour > 12) hour -= 12;
                                  }
                                  // Show time without minutes if it's on the hour
                                  timeFormatted = minute === 0 ? `${hour}${period}` : `${hour}:${minute.toString().padStart(2, '0')}${period}`;
                                }
                              } catch (e) {
                                timeFormatted = time24;
                              }
                            }
                            return timeFormatted;
                          }).filter(Boolean).join(', ') : []
                        ).filter(arr => arr.length > 0).join(', ')
                      }
                    </div>
                  )}
                  <div><strong>Catering Type:</strong> {order.catering_type || 'N/A'}</div>
                  <div><strong>Status:</strong> <Badge variant={order.status === 'confirmed' ? 'default' : 'outline'} className={order.status === 'confirmed' ? 'bg-green-600' : 'border-orange-300'}>{order.status || 'N/A'}</Badge></div>
                  <div className="pt-2 border-t border-blue-200 mt-2">
                    <div><strong>Created:</strong> {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}</div>
                    <div><strong>Created By:</strong> {order.customer_info?.prepared_by || 'N/A'}</div>
                  </div>
                  {order.updated_by && order.updated_by !== '' && (
                    <div className="pt-2 border-t border-blue-200 mt-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-orange-500 text-orange-700 bg-orange-50">Edited</Badge>
                      </div>
                      <div className="mt-1"><strong>Last Updated:</strong> {order.updated_at ? new Date(order.updated_at).toLocaleString() : 'N/A'}</div>
                      <div><strong>Updated By:</strong> {order.updated_by}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="card-bawarchi">
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: 'var(--spacing-4)', color: 'var(--bawarchi-primary)' }}>Menu Items</h3>
              <div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-orange-200">
                      <th className="text-left p-2">Item</th>
                      <th className="text-left p-2">Category</th>
                      <th className="text-center p-2">Unit Size</th>
                      <th className="text-center p-2" style={{minWidth: '80px'}}>Quantity</th>
                      <th className="text-right p-2">Unit Price</th>
                      <th className="text-right p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="p-2">
                          <div>{item.menu_item_name || item.name || 'N/A'}</div>
                          {item.day_number && <div className="text-xs text-gray-500">Day {item.day_number} - {item.meal_type || ''}</div>}
                          {item.notes && <div className="text-xs text-gray-500 italic">{item.notes}</div>}
                        </td>
                        <td className="p-2 text-sm">{item.category || 'N/A'}</td>
                        <td className="p-2 text-center text-sm">
                          {item.unit_of_measure && item.tray_size 
                            ? `${item.unit_of_measure}-${item.tray_size.replace('_', ' ')}` 
                            : (item.tray_size ? item.tray_size.replace('_', ' ') : 'N/A')}
                        </td>
                        <td className="p-2 text-center" style={{minWidth: '80px'}}>{item.quantity || 0}</td>
                        <td className="p-2 text-right">${Math.floor(item.unit_price || item.price || 0)}</td>
                        <td className="p-2 text-right font-semibold">${Math.floor(item.total_price || ((item.quantity || 0) * (item.unit_price || item.price || 0)))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="card-bawarchi">
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: 'var(--spacing-4)', color: 'var(--bawarchi-success)' }}>Pricing Summary</h3>
              <div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${Math.floor(order.subtotal || 0)}</span>
                  </div>
                  {(order.service_fee || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Service Fee {order.service_fee_description && `(${order.service_fee_description})`}:</span>
                      <span>${Math.floor(order.service_fee || 0)}</span>
                    </div>
                  )}
                  {(order.discount_amount || 0) > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Discount ({order.discount_type || 'N/A'}):</span>
                      <span>-${Math.floor(order.discount_amount || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold border-t border-green-300 pt-2 mt-2">
                    <span>Total Amount:</span>
                    <span>${Math.floor(order.total_amount || 0)}</span>
                  </div>
                  {(order.advance_payment || 0) > 0 && (
                    <>
                      <div className="flex justify-between text-green-600">
                        <span>Advance Paid:</span>
                        <span>${Math.floor(order.advance_payment || 0)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-semibold">
                        <span>Balance Due:</span>
                        <span>${Math.floor(order.balance_amount || 0)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Role Selection Component
// Auth components now imported from ./components/auth/

// Admin Password Change Component
export default OrderView;
